import { css, cva } from '@styled-system/css';
import { ImageOff, Play } from 'lucide-react';
import { type FC, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import type { GetTrendListQuery, TrendListKind } from '@/shared/hooks/queries/generated';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

// ============================================================================
// Tableau Trend — une niche par ligne, MÊME idiome visuel que le tableau des
// niches d'une recherche produit (NichesTable) : cellules mono, % de vente à
// palier « froid », chip bordée pour la métrique vedette, actions en boutons
// icône avec tooltip, clic de ligne = action principale.
// Règles du changelog 2026-09-17 conservées : `name` affiché tel quel (style
// selon nameSource), jamais de déduplication, image seed -> repli dernière
// vente, growthPct libellé différemment selon la liste. Le rang est FIGÉ sur
// l'ordre du classement serveur, insensible au tri des colonnes.
// ============================================================================

export type TrendNiche = GetTrendListQuery['getTrendList']['niches'][0];

type TrendRow = TrendNiche & { rank: number };

const nicheCell = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  minWidth: 0,
});

const nicheImg = css({
  width: '28px',
  height: '28px',
  borderRadius: 'md',
  objectFit: 'cover',
  bg: 'surface.sunken',
  flexShrink: 0,
});

const nicheImgFallback = css({
  width: '28px',
  height: '28px',
  borderRadius: 'md',
  bg: 'surface.sunken',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

const nicheNameStack = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5',
  minWidth: 0,
  // La colonne niche est « flex » (meta.flex du DataTable) : la pile prend
  // toute la place que la colonne reçoit, et le nom se tronque en ellipse
  flex: 1,
});

const nicheName = cva({
  base: {
    fontSize: 'bodySm',
    fontWeight: 'medium',
    color: 'text.primary',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  variants: {
    // structured = intitulé composé par l'API (« Marque · Catégorie ») : sobre
    tone: {
      title: {},
      structured: { fontWeight: 'regular', color: 'text.secondary' },
    },
  },
  defaultVariants: { tone: 'title' },
});

const nichePath = css({
  fontSize: 'micro',
  color: 'text.tertiary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const monoCell = css({
  fontFamily: 'mono',
  fontSize: 'bodySm',
  color: 'text.primary',
  whiteSpace: 'nowrap',
});

const mutedMonoCell = css({
  fontFamily: 'mono',
  fontSize: 'bodySm',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
});

// % de vente : neutre (blanc semibold) — gris non-gras quand c'est mauvais.
const soldPctCell = cva({
  base: {
    fontFamily: 'mono',
    fontSize: 'bodySm',
    fontWeight: 'semibold',
    color: 'text.primary',
  },
  variants: {
    tier: {
      cold: { color: 'text.tertiary', fontWeight: 'regular' },
      normal: {},
    },
  },
  defaultVariants: { tier: 'normal' },
});

const soldPctTier = (pct: number | null | undefined): 'cold' | 'normal' =>
  pct == null || pct < 10 ? 'cold' : 'normal';

// Croissance en petit tag coloré — même dessin que le score des niches.
const growthCell = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1',
    paddingX: '3',
    paddingY: '0.5',
    borderRadius: 'full',
    borderWidth: '1px',
    borderStyle: 'solid',
    fontFamily: 'mono',
    fontSize: 'caption',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  variants: {
    tier: {
      down: { bg: 'error.soft', color: 'error.default', borderColor: 'error.border' },
      up: { bg: 'success.soft', color: 'success.default', borderColor: 'success.border' },
    },
  },
});

// ─── Carte mobile : toute la data d'une ligne, empilée verticalement ────────

const mobileCard = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4',
  padding: '4',
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'xl',
});

const mobileMetricsGrid = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '3',
});

const mobileMetric = css({ display: 'flex', flexDirection: 'column', gap: '0.5', minWidth: 0 });

const mobileMetricLabel = css({ fontSize: 'micro', color: 'text.tertiary', whiteSpace: 'nowrap' });

const formatMono = (value: number | null | undefined, digits = 0, suffix = ''): string =>
  value == null ? '—' : `${value.toFixed(value < 1 && value > 0 ? 2 : digits)}${suffix}`;

/** CA compact : « 9 630 € » / « 14,3 k€ » / « 488 k€ » / « 1,2 M€ ». */
const formatCompactRevenue = (amount: number | null | undefined, locale: string): string => {
  if (amount == null) return '—';
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toLocaleString(locale, { maximumFractionDigits: 1 })} M€`;
  if (amount >= 100_000) return `${Math.round(amount / 1_000).toLocaleString(locale)} k€`;
  if (amount >= 10_000) return `${(amount / 1_000).toLocaleString(locale, { maximumFractionDigits: 1 })} k€`;
  return `${Math.round(amount).toLocaleString(locale)} €`;
};

/** Sparkline SVG des 14 jours de ventes — aura dégradée sous la courbe. */
const Sparkline: FC<{ series: TrendNiche['series']; width?: string }> = ({ series, width }) => {
  const gradientId = useId();
  if (series.length < 2) return null;
  const w = 100;
  const h = 24;
  const max = Math.max(...series.map((p) => p.sales), 1);
  const points = series
    .map((p, i) => `${((i / (series.length - 1)) * w).toFixed(1)},${(h - 2 - (p.sales / max) * (h - 4)).toFixed(1)}`)
    .join(' ');
  return (
    <svg
      width={width ?? w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio={width ? 'none' : undefined}
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--colors-brand-primary)" stopOpacity="0.45" />
          <stop offset="100%" stopColor="var(--colors-brand-primary)" stopOpacity="0.04" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${points} ${w},${h}`} fill={`url(#${gradientId})`} />
      <polyline
        points={points}
        fill="none"
        stroke="var(--colors-brand-primary)"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

interface TrendTableProps {
  /** `rank` fourni par l'appelant quand la liste est pré-filtrée (rang serveur conservé). */
  niches: (TrendNiche & { rank?: number })[];
  list: TrendListKind;
  /** Recherche texte (filtre global du DataTable). */
  globalFilter?: string;
  onOpen: (niche: TrendNiche) => void;
  /** Clic de ligne : popup de détail de la niche. */
  onShowDetail: (niche: TrendNiche) => void;
}

export const TrendTable: FC<TrendTableProps> = ({ niches, list, globalFilter, onOpen, onShowDetail }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';
  const isMobile = useIsMobile();

  // Rang figé sur l'ordre du classement serveur, insensible au tri colonnes
  const rows = useMemo<TrendRow[]>(
    () => niches.map((niche, index) => ({ ...niche, rank: niche.rank ?? index + 1 })),
    [niches],
  );

  const formatHours = (hours: number | null | undefined): string => {
    if (hours == null) return '—';
    const day = t('ui.timeUnits.day');
    const hour = t('ui.timeUnits.hour');
    const days = Math.floor(hours / 24);
    const rest = Math.round(hours % 24);
    if (days === 0) return `${rest}${hour}`;
    return rest > 0 ? `${days}${day} ${rest}${hour}` : `${days}${day}`;
  };

  const columns = useMemo<DataTableColumn<TrendRow>[]>(
    () => [
      {
        id: 'rank',
        header: '#',
        accessorFn: (row: TrendRow) => row.rank,
        enableSorting: true,
        size: 36,
        cell: ({ row }) => <span className={mutedMonoCell}>{row.original.rank}</span>,
      },
      {
        id: 'niche',
        header: t('niches.colNiche'),
        // L'accessor alimente AUSSI le filtre global (nom + marque + catégorie)
        accessorFn: (row: TrendRow) =>
          [row.name, row.brand?.name, row.catalog?.leafName ?? row.catalog?.level1Name ?? row.catalog?.rootName]
            .filter(Boolean)
            .join(' '),
        enableSorting: true,
        // Colonne flexible : absorbe la largeur restante pour que le tableau
        // tienne dans sa card — les noms trop longs passent en ellipse
        meta: { flex: true },
        cell: ({ row }) => {
          const niche = row.original;
          const imageUrl = niche.seed?.imageUrl ?? niche.lastSales[0]?.imageUrl ?? null;
          const rawContext = [
            niche.brand?.name,
            niche.catalog?.leafName ?? niche.catalog?.level1Name ?? niche.catalog?.rootName,
          ]
            .filter(Boolean)
            .join(' · ');
          // Nom « structured » = déjà « Marque · Catégorie » : ne pas le répéter
          const context = rawContext.trim().toLowerCase() === niche.name.trim().toLowerCase() ? '' : rawContext;
          return (
            <div className={nicheCell}>
              {imageUrl ? (
                <img src={imageUrl} alt="" className={nicheImg} loading="lazy" />
              ) : (
                <span className={nicheImgFallback}>
                  <ImageOff size={13} strokeWidth={1.75} />
                </span>
              )}
              <div className={nicheNameStack}>
                <span className={nicheName({ tone: niche.nameSource === 'structured' ? 'structured' : 'title' })}>
                  {niche.name}
                </span>
                {context && <span className={nichePath}>{context}</span>}
              </div>
            </div>
          );
        },
      },
      {
        id: 'caPerMonth',
        header: t('trend.caPerMonth'),
        accessorFn: (row: TrendRow) => row.metrics.caPerMonth ?? -1,
        size: 90,
        cell: ({ row }) => (
          <span className={monoCell}>{formatCompactRevenue(row.original.metrics.caPerMonth, locale)}</span>
        ),
        enableSorting: true,
        sortingFn: (a, b) => (a.original.metrics.caPerMonth ?? -1) - (b.original.metrics.caPerMonth ?? -1),
      },
      {
        id: 'soldPct',
        header: t('niches.colSoldPct'),
        accessorFn: (row: TrendRow) => row.metrics.soldPct ?? -1,
        size: 80,
        cell: ({ row }) => (
          <span className={soldPctCell({ tier: soldPctTier(row.original.metrics.soldPct) })}>
            {row.original.metrics.soldPct == null ? '—' : `${Math.round(row.original.metrics.soldPct)}%`}
          </span>
        ),
        enableSorting: true,
        sortingFn: (a, b) => (a.original.metrics.soldPct ?? -1) - (b.original.metrics.soldPct ?? -1),
      },
      {
        id: 'salesPerDay',
        header: t('niches.colSoldPerDay'),
        accessorFn: (row: TrendRow) => row.metrics.salesPerDay ?? -1,
        size: 85,
        cell: ({ row }) => <span className={mutedMonoCell}>{formatMono(row.original.metrics.salesPerDay, 1)}</span>,
        enableSorting: true,
        sortingFn: (a, b) => (a.original.metrics.salesPerDay ?? -1) - (b.original.metrics.salesPerDay ?? -1),
      },
      {
        id: 'avgPrice',
        header: t('niches.colAvgPrice'),
        accessorFn: (row: TrendRow) => row.metrics.avgPrice ?? -1,
        size: 95,
        cell: ({ row }) => (
          <span className={monoCell}>
            {row.original.metrics.avgPrice == null
              ? '—'
              : `${row.original.metrics.avgPrice.toFixed(2).replace('.', ',')} €`}
          </span>
        ),
        enableSorting: true,
        sortingFn: (a, b) => (a.original.metrics.avgPrice ?? -1) - (b.original.metrics.avgPrice ?? -1),
      },
      {
        id: 'timeToSell',
        header: t('niches.colTimeToSell'),
        accessorFn: (row: TrendRow) => row.metrics.timeToSellHours ?? Number.MAX_SAFE_INTEGER,
        size: 95,
        cell: ({ row }) => <span className={mutedMonoCell}>{formatHours(row.original.metrics.timeToSellHours)}</span>,
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.metrics.timeToSellHours ?? Infinity) - (b.original.metrics.timeToSellHours ?? Infinity),
      },
      {
        id: 'productCount',
        header: t('trend.productCount'),
        accessorFn: (row: TrendRow) => row.metrics.productCount,
        size: 75,
        cell: ({ row }) => <span className={mutedMonoCell}>{formatMono(row.original.metrics.productCount)}</span>,
        enableSorting: true,
        sortingFn: (a, b) => a.original.metrics.productCount - b.original.metrics.productCount,
      },
      {
        id: 'growth',
        header: list === 'top' ? t('trend.colGrowthWeekly') : t('trend.colGrowthTrend'),
        accessorFn: (row: TrendRow) => row.metrics.growthPct ?? -Infinity,
        size: 100,
        cell: ({ row }) => {
          const growth = row.original.metrics.growthPct;
          if (growth == null) return <span className={mutedMonoCell}>—</span>;
          return (
            <Tooltip label={t(list === 'top' ? 'trend.growthWeeklyHint' : 'trend.growthTrendHint')}>
              <span className={growthCell({ tier: growth >= 0 ? 'up' : 'down' })}>
                {`${growth >= 0 ? '+' : ''}${growth.toLocaleString(locale, { maximumFractionDigits: 0 })}%`}
              </span>
            </Tooltip>
          );
        },
        enableSorting: true,
        sortingFn: (a, b) => (a.original.metrics.growthPct ?? -Infinity) - (b.original.metrics.growthPct ?? -Infinity),
      },
      {
        id: 'series',
        header: t('trend.colSeries'),
        size: 95,
        cell: ({ row }) => <Sparkline series={row.original.series} />,
      },
      {
        id: 'actions',
        header: '',
        size: 56,
        cell: ({ row }) => (
          <div className={css({ display: 'flex', alignItems: 'center', gap: '2', justifyContent: 'flex-end' })}>
            {/* Masqué sans relaunchCriteria serveur — pas de reconstruction de secours */}
            {row.original.relaunchCriteria != null && (
              <Tooltip label={t('trend.openAnalysis')}>
                <span>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Play size={12} strokeWidth={2} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(row.original);
                    }}
                    aria-label={t('trend.openAnalysis')}
                  />
                </span>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, locale, list, onOpen],
  );

  // ─── Mobile : cartes empilées avec TOUTE la data (le tableau ne peut pas
  // tout montrer sur un petit écran — on prend la place verticalement) ───
  if (isMobile) {
    const query = (globalFilter ?? '').trim().toLowerCase();
    const filtered = query
      ? rows.filter((row) => {
          const context = [row.brand?.name, row.catalog?.leafName, row.catalog?.level1Name, row.catalog?.rootName]
            .filter(Boolean)
            .join(' ');
          return `${row.name} ${context}`.toLowerCase().includes(query);
        })
      : rows;
    return (
      <div className={css({ display: 'flex', flexDirection: 'column', gap: '3' })}>
        {filtered.map((row) => {
          const imageUrl = row.seed?.imageUrl ?? row.lastSales[0]?.imageUrl ?? null;
          const rawContext = [
            row.brand?.name,
            row.catalog?.leafName ?? row.catalog?.level1Name ?? row.catalog?.rootName,
          ]
            .filter(Boolean)
            .join(' · ');
          const context = rawContext.trim().toLowerCase() === row.name.trim().toLowerCase() ? '' : rawContext;
          const growth = row.metrics.growthPct;
          const metrics: { label: string; value: string }[] = [
            { label: t('trend.caPerMonth'), value: formatCompactRevenue(row.metrics.caPerMonth, locale) },
            {
              label: t('niches.colSoldPct'),
              value: row.metrics.soldPct == null ? '—' : `${Math.round(row.metrics.soldPct)}%`,
            },
            { label: t('niches.colSoldPerDay'), value: formatMono(row.metrics.salesPerDay, 1) },
            {
              label: t('niches.colAvgPrice'),
              value: row.metrics.avgPrice == null ? '—' : `${row.metrics.avgPrice.toFixed(2).replace('.', ',')} €`,
            },
            { label: t('niches.colTimeToSell'), value: formatHours(row.metrics.timeToSellHours) },
            { label: t('trend.productCount'), value: formatMono(row.metrics.productCount) },
          ];
          return (
            <div key={row.id} className={mobileCard} onClick={() => onShowDetail(row)} role="button" tabIndex={0}>
              <div className={css({ display: 'flex', alignItems: 'center', gap: '3' })}>
                <span className={mutedMonoCell}>{row.rank}</span>
                {imageUrl ? (
                  <img src={imageUrl} alt="" className={nicheImg} loading="lazy" />
                ) : (
                  <span className={nicheImgFallback}>
                    <ImageOff size={13} strokeWidth={1.75} />
                  </span>
                )}
                <div className={css({ flex: 1, minWidth: 0 })}>
                  <div className={nicheName({ tone: row.nameSource === 'structured' ? 'structured' : 'title' })}>
                    {row.name}
                  </div>
                  {context && <div className={nichePath}>{context}</div>}
                </div>
                {growth != null && (
                  <span className={growthCell({ tier: growth >= 0 ? 'up' : 'down' })}>
                    {`${growth >= 0 ? '+' : ''}${growth.toLocaleString(locale, { maximumFractionDigits: 0 })}%`}
                  </span>
                )}
              </div>

              <div className={mobileMetricsGrid}>
                {metrics.map((metric) => (
                  <div key={metric.label} className={mobileMetric}>
                    <span className={mobileMetricLabel}>{metric.label}</span>
                    <span className={monoCell}>{metric.value}</span>
                  </div>
                ))}
              </div>

              <Sparkline series={row.series} width="100%" />

              {row.relaunchCriteria != null && (
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Play size={12} strokeWidth={2} />}
                  className={css({ width: 'full' })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(row);
                  }}
                >
                  {t('trend.openAnalysis')}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <DataTable
      data={rows}
      columns={columns}
      getRowId={(row) => row.id}
      globalFilter={globalFilter}
      emptyTitle={t('niches.emptyTitle')}
      emptyHint={t('trend.emptyHint')}
      onRowClick={(row) => onShowDetail(row)}
    />
  );
};
